import {
  ChangeDetectorRef,
  Component,
  EventEmitter,
  forwardRef,
  HostBinding,
  inject,
  Input,
  OnInit,
  Output,
} from '@angular/core';
import {
  AbstractControl,
  ControlValueAccessor,
  FormControl,
  FormGroup,
  NG_VALIDATORS,
  NG_VALUE_ACCESSOR,
  UntypedFormBuilder,
  ValidationErrors,
  Validator,
  Validators,
} from '@angular/forms';
import { CashbackFormData } from '@features/cashback/types';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { TranslateService } from '@ngx-translate/core';
import { CurrencyConversionApiService } from '@rd/core/services/currency-conversion-api/currency-conversion-api.service';
import { CurrencyService } from '@rd/core/services/currency/currency.service';
import { LoyaltyCurrencyService } from '@rd/core/services/loyalty-currency/loyalty-currency.service';
import { NumberService } from '@rd/core/services/number/number.service';
import { UrlUtilsService } from '@rd/core/services/url-utils/url-utils.service';
import {
  CounterConfig,
  CounterTranslationsConfig,
  CurrencyConversion,
  LoyaltyProgram,
} from '@rd/types';
import {
  ALL_NUMBERS_WITH_DECIMAL_REGEX,
  NUMBER_ONLY_REGEX,
} from '@rd/utils/regex.utils';
import { EMPTY, Subject } from 'rxjs';
import {
  catchError,
  debounceTime,
  distinctUntilChanged,
  filter,
  switchMap,
  tap,
} from 'rxjs/operators';

function getSourceCurrency() {
  const loyaltyCurrencyService = inject(LoyaltyCurrencyService);

  return () => {
    return loyaltyCurrencyService.loyaltyCurrencySelected;
  };
}

@UntilDestroy()
@Component({
  selector: 'rd-rcv2-checkout-transfer-amount-form',
  templateUrl: './checkout-transfer-amount-form.component.html',
  styleUrls: ['./checkout-transfer-amount-form.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => RcV2CheckoutTransferAmountFormComponent),
      multi: true,
    },
    {
      provide: NG_VALIDATORS,
      useExisting: forwardRef(() => RcV2CheckoutTransferAmountFormComponent),
      multi: true,
    },
  ],
})
export class RcV2CheckoutTransferAmountFormComponent
  implements OnInit, ControlValueAccessor, Validator
{
  @HostBinding('class.rd-rcv2-checkout-transfer-amount-form')
  componentClass = true;

  private cdRef = inject(ChangeDetectorRef);
  private fb = inject(UntypedFormBuilder);
  private currencyConversionApiService = inject(CurrencyConversionApiService);
  private currencyService = inject(CurrencyService);
  private numberService = inject(NumberService);
  private urlUtilsService = inject(UrlUtilsService);
  private translateService = inject(TranslateService);

  @Input() currencyConversionLoading: boolean;
  @Input() formData: CashbackFormData;
  @Input() redeemCashLoyaltyProgram: LoyaltyProgram;
  @Input() readOnlyCashFormField: boolean;
  @Input() isSubmitted: Subject<boolean>;
  @Output()
  updateCurrencyConversionLoading: EventEmitter<boolean> = new EventEmitter<boolean>();

  private getSourceCurrency = getSourceCurrency();

  loyaltyCurrencyName = this.getSourceCurrency()?.name ?? '';

  targetCurrency = this.currencyService.currentCurrencySymbol();

  transferIconUrl = this.urlUtilsService.imageUrl(
    '/images/rd/icn-transfer.svg',
  );

  transferAmountForm: FormGroup<{
    sourceAmount: FormControl<number>;
    targetAmount: FormControl<number>;
  }>;

  counterConfig: CounterConfig;

  counterTranslationsConfig: Partial<CounterTranslationsConfig>;

  propagateChange: (value: any) => void;
  onTouched: () => void = () => {};

  get targetDenomination(): number {
    return (
      // @ts-ignore
      this.redeemCashLoyaltyProgram?.transferRate.denomination *
      // @ts-ignore
      this.redeemCashLoyaltyProgram?.transferRate.exchangeRate
    );
  }

  ngOnInit(): void {
    this.setupCounterConfig();
    this.setupTransferForm();
    this.setupOnSubmittingListener();
    this.setupTransferAmountFormListeners();
    this.setInitialAmountFields();
  }

  private setupCounterConfig() {
    this.counterConfig = {
      maxValue:
        this.redeemCashLoyaltyProgram?.transferRate.maximumTransfer ??
        10_000_000,
      minValue:
        this.redeemCashLoyaltyProgram?.transferRate.minimumTransfer ?? 0,
      valuePerChange: this.redeemCashLoyaltyProgram?.transferRate.denomination,
      formatValue: false,
      aria_unit: this.loyaltyCurrencyName,
    };

    this.counterTranslationsConfig = {
      hintTranslation: this.translateService.instant(
        'redeem_cash_v2.checkout.hint.must_be_multiples_of',
        {
          amount: this.numberService.formatNumber(
            // @ts-ignore
            this.redeemCashLoyaltyProgram?.transferRate.denomination,
          ),
        },
      ),
      minusButtonAriaLabel: this.translateService.instant(
        'accessibility.redeem_cash_v2.checkout.subtract',
        { loyaltyCurrency: this.loyaltyCurrencyName },
      ),
      plusButtonAriaLabel: this.translateService.instant(
        'accessibility.redeem_cash_v2.checkout.add',
        { loyaltyCurrency: this.loyaltyCurrencyName },
      ),
    };
  }

  private setupTransferForm() {
    this.transferAmountForm = this.fb.group({
      sourceAmount: [
        null,
        {
          validators: [
            Validators.required,
            Validators.pattern(NUMBER_ONLY_REGEX),
          ],
        },
      ],
      targetAmount: [
        null,
        {
          validators: [
            Validators.required,
            Validators.pattern(
              this.readOnlyCashFormField
                ? ALL_NUMBERS_WITH_DECIMAL_REGEX
                : NUMBER_ONLY_REGEX,
            ),
          ],
        },
      ],
    });
  }

  private setupOnSubmittingListener(): void {
    this.isSubmitted.pipe(untilDestroyed(this)).subscribe((event) => {
      if (event) {
        this.setAmountFormRequiredFields();
        this.transferAmountForm.markAllAsTouched();
        this.cdRef.markForCheck();
      }
    });
  }

  private setupTransferAmountFormListeners(): void {
    const { sourceAmount: sourceAmountField, targetAmount: targetAmountField } =
      this.transferAmountForm.controls;

    sourceAmountField.valueChanges
      .pipe(
        filter(
          () => sourceAmountField.value != null && sourceAmountField.valid,
        ),
        debounceTime(200),
        distinctUntilChanged(),
        tap(() => {
          this.emitUpdateCurrencyConversionLoading(true);
        }),
        switchMap((sourceAmount: number) =>
          this.currencyConversionApiService
            .postCurrencyConversion({
              homeCurrencyId: this.getSourceCurrency()?.id ?? '',
              targetCurrencyId:
                this.redeemCashLoyaltyProgram?.transferRate.loyaltyCurrencyId,
              redemptionAmount: sourceAmount,
            })
            .pipe(
              catchError(() => {
                this.emitUpdateCurrencyConversionLoading(false);

                return EMPTY;
              }),
            ),
        ),
        untilDestroyed(this),
      )
      .subscribe((currencyConversion) =>
        this.applyCurrencyConversionValues(currencyConversion),
      );

    targetAmountField.valueChanges
      .pipe(
        filter(
          () => targetAmountField.value != null && targetAmountField.valid,
        ),
        debounceTime(200),
        distinctUntilChanged(),
        tap(() => {
          this.emitUpdateCurrencyConversionLoading(true);
        }),
        switchMap((targetAmount: number) =>
          this.currencyConversionApiService
            .postCurrencyConversion({
              homeCurrencyId: this.getSourceCurrency()?.id ?? '',
              targetCurrencyId:
                this.redeemCashLoyaltyProgram?.transferRate.loyaltyCurrencyId,
              transferAmount: targetAmount,
            })
            .pipe(
              catchError(() => {
                this.emitUpdateCurrencyConversionLoading(false);

                return EMPTY;
              }),
            ),
        ),
        untilDestroyed(this),
      )
      .subscribe((currencyConversion) =>
        this.applyCurrencyConversionValues(currencyConversion),
      );
  }

  // Prepopulate sourceAmount if formData is populated. Don't prepopulate targetAmount, as the
  // async validator will prepopulate for us.
  private setInitialAmountFields(): void {
    this.transferAmountForm.patchValue({
      sourceAmount:
        this.formData?.sourceAmount ??
        this.redeemCashLoyaltyProgram?.transferRate.minimumTransfer,
    });
  }

  /*
  ====================================
    CVA Functions
  ====================================
  */

  writeValue(val: any): void {
    if (!!val) {
      this.transferAmountForm.setValue(val, { emitEvent: false });
    }
  }

  registerOnChange(fn: any): void {
    this.propagateChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  setDisabledState?(isDisabled: boolean): void {
    isDisabled
      ? this.transferAmountForm?.disable()
      : this.transferAmountForm?.enable();
  }

  validate(c: AbstractControl): ValidationErrors | null {
    return this.transferAmountForm.valid
      ? null
      : { invalidTransferAmountForm: true };
  }

  /*
  ====================================
    Form Error Checkers
  ====================================
  */

  isSourceAmountEmpty(): boolean {
    return this.validateAfterFormIsTouched(() =>
      (this.transferAmountForm.get('sourceAmount') as AbstractControl).hasError(
        'required',
      ),
    );
  }

  isSourceAmountNotNumber(): boolean {
    return this.validateAfterFormIsTouched(() =>
      (this.transferAmountForm.get('sourceAmount') as AbstractControl).hasError(
        'pattern',
      ),
    );
  }

  isTargetAmountEmpty(): boolean {
    return this.validateAfterFormIsTouched(() =>
      (this.transferAmountForm.get('targetAmount') as AbstractControl).hasError(
        'required',
      ),
    );
  }

  isTargetAmountNotNumber(): boolean {
    return this.validateAfterFormIsTouched(() =>
      (this.transferAmountForm.get('targetAmount') as AbstractControl).hasError(
        'pattern',
      ),
    );
  }

  // Must ensure form is touched already, as specs fail unless this check is present
  private validateAfterFormIsTouched(validateFn: () => boolean): boolean {
    return this.transferAmountForm.touched && validateFn();
  }

  /*
  ====================================
    Helper Functions
  ====================================
  */

  private setAmountFormRequiredFields(): void {
    const { sourceAmount, targetAmount } = this.transferAmountForm.controls;

    sourceAmount.updateValueAndValidity();
    targetAmount.updateValueAndValidity();
  }

  private applyCurrencyConversionValues({
    redemptionAmount,
    transferAmountBase,
  }: CurrencyConversion): void {
    this.transferAmountForm.setValue(
      {
        sourceAmount: redemptionAmount,
        targetAmount: transferAmountBase,
      },
      {
        emitEvent: false,
      },
    );

    this.propagateChange(this.transferAmountForm.value);

    this.emitUpdateCurrencyConversionLoading(false);
    this.cdRef.markForCheck();
  }

  emitUpdateCurrencyConversionLoading(loading: boolean): void {
    this.updateCurrencyConversionLoading.emit(loading);
  }
}
